import { useEffect, useRef, useState } from 'react';

interface UseScrollEdgeOptions {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

interface UseScrollEdgeReturn {
  topTriggerRef: React.RefObject<HTMLDivElement>;
  bottomTriggerRef: React.RefObject<HTMLDivElement>;
  isTopReached: boolean;
  isBottomReached: boolean;
}

// 스크롤 컨테이너 내부의 상단/하단 기준점(sentinel) 노출 여부를 관찰해
// 스크롤 경계 도달 상태 기반 UI(그라데이션, 그림자, 버튼 노출 등)에서 사용한다.
const useScrollEdge = ({ scrollContainerRef }: UseScrollEdgeOptions): UseScrollEdgeReturn => {
  const [isTopReached, setIsTopReached] = useState(true);
  const [isBottomReached, setIsBottomReached] = useState(false);

  // 스크롤 영역의 시작/끝에 배치할 기준점 요소 ref
  const topTriggerRef = useRef<HTMLDivElement>(null);
  const bottomTriggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const topTrigger = topTriggerRef.current;
    const bottomTrigger = bottomTriggerRef.current;

    if (!scrollContainer || !topTrigger || !bottomTrigger) return;

    // 컨테이너 내부에서 기준점이 보이는지 감지해 상단/하단 도달 상태를 갱신한다.
    const topObserver = new IntersectionObserver(
      ([entry]) => setIsTopReached(entry.isIntersecting),
      { root: scrollContainer }
    );

    const bottomObserver = new IntersectionObserver(
      ([entry]) => setIsBottomReached(entry.isIntersecting),
      { root: scrollContainer }
    );

    topObserver.observe(topTrigger);
    bottomObserver.observe(bottomTrigger);

    return () => {
      topObserver.disconnect();
      bottomObserver.disconnect();
    };
  }, [scrollContainerRef]);

  return {
    topTriggerRef,
    bottomTriggerRef,
    isTopReached,
    isBottomReached,
  };
};

export default useScrollEdge;
